import type * as Sentry from '@sentry/browser'
import { parseClientEnv, scrubEmails } from '@rollcall/contracts'

/**
 * Мониторинг ошибок клиента (D17: self-hosted GlitchTip).
 *
 * GlitchTip Sentry-совместим, поэтому здесь обычный `@sentry/browser`.
 * Остальной код клиента зовёт только `reportError`.
 *
 * SDK грузится отдельным чанком и только когда он действительно нужен:
 * в главном чанке он занимал 188 кБ из 1323 кБ — четверть того, что
 * скачивает человек, ради кода, который до первой ошибки не работает
 * (`apps/web/bundle-report.md`). Импорт типов остаётся статическим:
 * типы стираются на сборке и в чанк не попадают.
 *
 * Email в отчёты не попадает (security-checklist.md, п. 3): вычищается
 * и переданный контекст, и всё событие целиком перед отправкой —
 * включая текст ошибки, стек и данные запросов, которые SDK собирает
 * сам.
 */

export type ErrorContext = Readonly<Record<string, string | number | boolean>>

type Reported = { readonly error: unknown; readonly context: ErrorContext }

let sdk: typeof Sentry | null = null
let loading: Promise<void> | null = null

/**
 * Ошибки, случившиеся до загрузки SDK. Очередь короткая и по делу:
 * ошибка при старте — самая ценная из всех, терять её нельзя.
 */
const pending: Reported[] = []

/** Настройки SDK отдельно — чтобы `beforeSend` можно было проверить тестом. */
export function errorMonitoringOptions(
  dsn: string | undefined,
  mode: string,
): Sentry.BrowserOptions {
  return {
    dsn,
    environment: mode,
    // SDK не должен собирать данные пользователя и заголовки сам.
    sendDefaultPii: false,
    tracesSampleRate: 0,
    beforeSend: (event) => scrubEmails(event),
    beforeBreadcrumb: (breadcrumb) => scrubEmails(breadcrumb),
  }
}

function send(sentry: typeof Sentry, { error, context }: Reported): void {
  sentry.captureException(error, { extra: { ...context } })
}

export function reportError(error: unknown, context: ErrorContext = {}): void {
  const safeContext = scrubEmails(context)

  if (sdk !== null) {
    send(sdk, { error, context: safeContext })
    return
  }

  if (loading !== null) {
    pending.push({ error, context: safeContext })
    return
  }

  const description =
    error instanceof Error ? (error.stack ?? error.message) : String(error)
  console.error('[error]', safeContext, scrubEmails(description))
}

/** Загрузка SDK отдельным чанком и отправка накопленного. */
async function startSdk(dsn: string, mode: string): Promise<void> {
  const sentry = await import('@sentry/browser')
  sentry.init(errorMonitoringOptions(dsn, mode))
  sdk = sentry
  while (pending.length > 0) {
    const item = pending.shift()
    if (item !== undefined) send(sentry, item)
  }
}

/**
 * Инициализация при старте приложения.
 *
 * Окружение клиента проверяется той же схемой из contracts, что
 * и серверное: при `DEPLOY_ENV=production` DSN обязателен, при
 * `staging` его может не быть — тогда ошибки только в консоли (D26).
 * Проверка синхронная: прод без DSN обязан падать на старте, а не после
 * загрузки чанка.
 *
 * `window.error` и `unhandledrejection` слушает сам SDK; когда он
 * выключен, слушатели ставятся вручную, чтобы поведение не разъезжалось
 * между режимами.
 */
export function initErrorMonitoring(
  source: Record<string, string | undefined> = {
    NODE_ENV: import.meta.env.MODE,
    DEPLOY_ENV: import.meta.env.DEPLOY_ENV,
    VITE_ERROR_MONITORING_DSN: import.meta.env.VITE_ERROR_MONITORING_DSN,
  },
): void {
  const parsed = parseClientEnv(source)

  if (!parsed.ok) {
    // Сюда попадаем при `DEPLOY_ENV=production` без DSN и при
    // неизвестном или незаданном `DEPLOY_ENV` (D26). Приложение падает
    // при старте, как и сервер в такой же ситуации
    // (security-checklist.md, п. 1): собранный прод без сбора ошибок
    // должен обнаружиться сразу, а не через неделю тишины в панели.
    const problems = parsed.problems
      .map((problem) => `${problem.variable}: ${problem.message}`)
      .join('; ')
    throw new Error(`Окружение клиента не прошло проверку — ${problems}`)
  }

  const dsn = parsed.env.VITE_ERROR_MONITORING_DSN
  if (dsn === undefined) {
    console.warn(
      `мониторинг ошибок выключен: VITE_ERROR_MONITORING_DSN не задан ` +
        `при DEPLOY_ENV=${parsed.env.DEPLOY_ENV}. Ошибки видны только ` +
        `в консоли браузера (D26; при DEPLOY_ENV=production сюда ` +
        `не доходят)`,
    )
    installFallbackListeners()
    return
  }

  // Окружение в отчётах — `DEPLOY_ENV`, а не режим сборки: `production`
  // стоит и на стенде, и в проде, и отчёты обоих легли бы в один поток.
  loading = startSdk(dsn, parsed.env.DEPLOY_ENV).catch((error: unknown) => {
    // Не загрузился мониторинг — приложение всё равно работает,
    // а накопленное уходит в консоль, чтобы не пропасть совсем.
    loading = null
    console.error('[error] мониторинг не загрузился', error)
    for (const item of pending.splice(0)) {
      console.error('[error]', item.context, item.error)
    }
  })
}

function installFallbackListeners(): void {
  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, {
      scope: 'window.error',
      source: event.filename,
      line: event.lineno,
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { scope: 'unhandledrejection' })
  })
}
